import { Button } from '@native-instruments/webex-ni';
import {
  getIsShippable,
  getStockStatus,
} from 'dotcom-core/src/helpers/product';
import {
  useLoaderData,
  useNavigation,
  useRouteLoaderData,
  useSubmit,
} from 'react-router';
//import { useFireOnce } from 'dotcom-core/src/hooks/useFireOnce';
//import { useEffect } from 'react';
import type { ProductVariant } from '~/graphql';
import { useIntl } from '~/helpers/intl';
import { isTruthy } from '~/helpers/predicate';
import type {
  ProductInterface,
  //action,
  loader,
} from '~/routes/$language.pricing.$slug/route';

type Props = {
  product: ProductInterface;
  variant?: ProductVariant | null;
  quantity?: number | null;
};

export function AddToCartButton({
  product,
  variant,
  quantity = 1,
  ...props
}: Props) {
  const { addToCartSku } = useLoaderData<typeof loader>();
  const { t } = useIntl();
  const root = useRouteLoaderData('root');
  const navigation = useNavigation();
  const isLoading = navigation?.state === 'submitting';
  //const actionData = useActionData<typeof action>();
  const submit = useSubmit();
  //const navigate = useNavigate();
  //const fireEventOnce = useFireOnce();
  //const cartItemsWereAdded = isTruthy(
  //  actionData?.addProductsResult?.addProductsToCart?.cart?.items?.length,
  //);
  // const cartItem = isTruthy(variant) ? variant : product;

  const stockStatus = getStockStatus({ variant, product });
  const isOutOfStock = stockStatus === 'OUT_OF_STOCK';

  // @note: The code bellow works only on Remix.
  //        The problem now, is the cartId/guestCartId are stored as object with Remix,
  //        and simply as strings on ni-v1.
  //        We could uncomment it when cart logic on ni-v1 is migrated to remix as well.
  //
  // useEffect(() => {
  //   if (cartItemsWereAdded) {
  //     fireEventOnce(() => {
  //       pushEcommerceEvent('add_to_cart', {
  //         cart_id: root.cartId || '',
  //         customer_type: 'unknown', // @TODO [WWW-13133] User context,
  //         items: [
  //           {
  //             // item_id/item_name should come from the parent product,
  //             // but it's not available in the cart data yet.
  //             // @TODO [ECOM-85, ECOM-84]
  //             item_id: product.sku,
  //             item_name: product?.name || '',
  //             sku_id: cartItem.sku,
  //             sku_name: cartItem.name || '',
  //             price: cartItem?.price?.final.value || 0,
  //             quantity: 1,
  //           },
  //         ],
  //       });
  //     });

  //     //@TODO: Remove /shop/ once we migrate the cart page to ni-v2
  //     //Remember to also update the route name.
  //     return navigate(`/${root.language}/shop/cart/`);
  //   }
  // }, [
  //   cartItemsWereAdded,
  //   root.cartId,
  //   fireEventOnce,
  //   root.language,
  //   navigate,
  //   cartItem,
  // ]);

  if (!addToCartSku || quantity == null) {
    throw new Error(
      `Missing required data for AddToCartButton submit.
      Language: ${root.language}, Cart ID: ${root.cartId}, SKU: ${addToCartSku}, Quantity: ${quantity}`,
    );
  }

  const handleSubmit = () => {
    submit(
      {
        actionType: 'addProductsToCart',
        nativeId: root.nativeId ?? null,
        language: root.language,
        cartId: root.cartId,
        masterSku: product.sku,
        cartItems: [{ sku: addToCartSku, quantity }],
      },
      { method: 'post', encType: 'application/json' },
    );
  };

  const shouldRedirectToJPStore =
    getIsShippable({ product, variant }) && root.country === 'JP';

  if (shouldRedirectToJPStore) {
    return (
      <a
        href={constructJPStoreLink(product.sku)}
        className="webex-btn webex-btn--primary webex-btn--sm w-full"
        data-testid={`media-integration-button-${addToCartSku}`}
      >
        {t('pricing.actions.buy', {
          fallback: 'Buy online at MediaIntegration',
        })}
      </a>
    );
  }

  return (
    <Button
      size="small"
      fullWidth
      onClick={() => {
        handleSubmit();
      }}
      status={isOutOfStock ? 'disabled' : isLoading ? 'loading' : 'active'}
      variant="primary"
      {...props}
    >
      {t('components.QuickAddToCartButton.add', {
        fallback: 'Add to cart',
      })}
    </Button>
  );
}

function constructJPStoreLink(sku?: string | null) {
  if (!isTruthy(sku)) return;

  //@TODO [WWW-12033] Temporary solution until field is implemented in Magento
  const jpStoreLink =
    jpStoreLinks[sku as keyof typeof jpStoreLinks]?.jp_store_link ??
    'https://store.minet.jp/category/_NI/';

  const utmTags =
    '?utm_source=native-instruments.com&utm_medium=referral&utm_campaign=NI+SITE';

  return `${jpStoreLink}${utmTags}`;
}

const jpStoreLinks = {
  KOMPLETEKEYBOARDS88MK2: {
    title: 'KOMPLETE KONTROL S88',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0010.html',
  },
  MASCHINEPlus: {
    title: 'MASCHINE+',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0016.html',
  },
  KOMPLETEKEYBOARDS49Mk2: {
    title: 'KOMPLETE KONTROL S49',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0011.html',
  },
  KOMPLETEKONTROLM32: {
    title: 'KOMPLETE KONTROL M32',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0007.html',
  },
  KOMPLETEKEYBOARDS61Mk2: {
    title: 'KOMPLETE KONTROL S61',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0012.html',
  },
  KOMPLETEAUDIO6MK2: {
    title: 'KOMPLETE AUDIO 6',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0001.html',
  },
  MASCHINEMk3: {
    title: 'MASCHINE',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0014.html',
  },
  KOMPLETEKEYBOARDA61: {
    title: 'KOMPLETE KONTROL A61',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0006.html',
  },
  MASCHINEMikroMk3: {
    title: 'MASCHINE MIKRO',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0015.html',
  },
  KOMPLETEKEYBOARDA25: {
    title: 'KOMPLETE KONTROL A25',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0004.html',
  },
  KOMPLETEKEYBOARDA49: {
    title: 'KOMPLETE KONTROL A49',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0005.html',
  },
  KOMPLETEAUDIO1: {
    title: 'KOMPLETE AUDIO 1',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0002.html',
  },
  KOMPLETEAUDIO2: {
    title: 'KOMPLETE AUDIO 2',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0003.html',
  },
  TRAKTORKONTROLS2Mk3: {
    title: 'TRAKTOR KONTROL S2 MK3',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0018.html',
  },
  TRAKTORKONTROLX1MK3: {
    title: 'TRAKTOR X1 MK3',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0034.html',
  },
  ControlVinylsMk2: {
    title: 'TRAKTOR SCRATCH CONTROL VINYL BLACK MK2',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0027.html',
  },
  TRAKTORSCRATCHControlDiscsMK2: {
    title: 'TRAKTOR SCRATCH CONTROL CD MK2',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0032.html',
  },
  TRAKTORKONTROLF1: {
    title: 'TRAKTOR KONTROL F1',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0017.html',
  },
  TRAKTORKONTROLZ1: {
    title: 'TRAKTOR KONTROL Z1',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0023.html',
  },
  TRAKTORKONTROLS3: {
    title: 'TRAKTOR KONTROL S3',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0019.html',
  },
  TRAKTORKONTROLS4Mk3: {
    title: 'TRAKTOR KONTROL S4 MK3',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0020.html',
  },
  NIPOWERSUPPLY40W: {
    title: 'NI POWER SUPPLY (40 W)',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0033.html',
  },
  TRAKTORDJCable: {
    title: 'TRAKTOR DJ CABLE',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0025.html',
  },
  BUTTERRUGS: {
    title: 'TRAKTOR BUTTER RUG SLIPMATS',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0026.html',
  },
  TraktorKontrolBag: {
    title: 'TRAKTOR KONTROL BAG',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0024.html',
  },
  TRAKTORKONTROLS4MK3FLIGHTCASE: {
    title: 'TRAKTOR KONTROL S4 MK3 FLIGHTCASE',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0021.html',
  },
};
